import { useCallback, useEffect, useRef, useState } from "react";
import type { CropRect } from "../processing/types";
import { centerCropForAspect } from "../processing/crop";
import { decodeImage } from "../processing/decode";
import { CropFrame } from "./CropFrame";
import { originalBase } from "../naming/roomNames";
import { RoomNameInput } from "./RoomNameInput";

export interface GridFile {
  name: string;
  // Liefert die Bilddaten – lokal aus dem FileSystemFileHandle, bei SharePoint
  // per Download der vor-authentifizierten URL. Entkoppelt die Galerie von der Quelle.
  getFile: () => Promise<Blob>;
}

interface Props {
  files: GridFile[];
  // Manuell angepasste Ausschnitte (normalisiert) je Dateiname. Nicht enthaltene
  // Bilder werden beim Export mittig auf 4:3 zugeschnitten.
  onChange: (crops: Record<string, CropRect>) => void;
  // Eingegebene Namen je Datei (leer = Originalname) und die daraus aufgelösten
  // endgültigen Dateinamen (ohne Endung, ggf. durchnummeriert).
  names?: Record<string, string>;
  finalNames?: Record<string, string>;
  onNameChange?: (file: string, value: string) => void;
  // KI-Raumvorschläge: Vorschläge je Datei, laufende Anfragen und ein Statushinweis.
  aiSuggestions?: Record<string, string>;
  aiPending?: Set<string>;
  aiStatus?: string | null;
  // Wird für jede fertig dekodierte Vorschau aufgerufen (Startpunkt der KI-Erkennung).
  onPreview?: (file: string, bitmap: ImageBitmap) => void;
  aspect?: number;
  disabled?: boolean;
}

const MAX_PREVIEW = 1400;

async function decodePreview(name: string, blob: Blob): Promise<ImageBitmap> {
  let full: ImageBitmap;
  try {
    // Schnellweg: JPG/PNG/WebP kann der Browser direkt.
    full = await createImageBitmap(blob);
  } catch {
    // Formate ohne native Browser-Unterstützung (v. a. HEIC/HEIF): über die
    // App-Dekodierung (libheif) zu Pixeln machen und daraus ein Bitmap erzeugen.
    const imageData = await decodeImage(name, await blob.arrayBuffer());
    full = await createImageBitmap(imageData);
  }
  const m = Math.max(full.width, full.height);
  if (m <= MAX_PREVIEW) return full;
  const s = MAX_PREVIEW / m;
  const w = Math.round(full.width * s);
  const h = Math.round(full.height * s);
  const c = new OffscreenCanvas(w, h);
  const ctx = c.getContext("2d");
  if (!ctx) return full;
  ctx.drawImage(full, 0, 0, w, h);
  full.close();
  return await createImageBitmap(c);
}

export function ImageCropGrid({
  files,
  onChange,
  names = {},
  finalNames = {},
  onNameChange,
  aiSuggestions = {},
  aiPending,
  aiStatus,
  onPreview,
  aspect = 4 / 3,
  disabled,
}: Props) {
  const [bitmaps, setBitmaps] = useState<Record<string, ImageBitmap | "error">>({});
  const [crops, setCrops] = useState<Record<string, CropRect>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [tempCrop, setTempCrop] = useState<CropRect | null>(null);
  const bmpRef = useRef<Record<string, ImageBitmap>>({});
  const handleTemp = useCallback((c: CropRect) => setTempCrop(c), []);
  // Immer die aktuelle Callback-Version nutzen (KI-Freigabe kann nach dem Start eintreffen).
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;

  useEffect(() => {
    onChange(crops);
  }, [crops, onChange]);

  // Bilder nacheinander dekodieren (schont Speicher/Reaktivität bei vielen Dateien).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const f of files) {
        if (cancelled) return;
        try {
          const file = await f.getFile();
          const bmp = await decodePreview(f.name, file);
          if (cancelled) {
            bmp.close();
            return;
          }
          bmpRef.current[f.name] = bmp;
          setBitmaps((b) => ({ ...b, [f.name]: bmp }));
          onPreviewRef.current?.(f.name, bmp);
        } catch {
          setBitmaps((b) => ({ ...b, [f.name]: "error" }));
        }
      }
    })();
    return () => {
      cancelled = true;
      Object.values(bmpRef.current).forEach((b) => b.close());
      bmpRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const decoded = Object.keys(bitmaps).length;
  const selBmp = selected ? bitmaps[selected] : null;

  const commit = () => {
    if (selected && tempCrop) setCrops((c) => ({ ...c, [selected]: tempCrop }));
    setSelected(null);
  };
  const resetOne = () => {
    if (!selected) return;
    setCrops((c) => {
      const next = { ...c };
      delete next[selected];
      return next;
    });
    setSelected(null);
  };

  return (
    <div className="card">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <h2 className="text-lg font-semibold">Bilder auf 4:3 zuschneiden</h2>
        <span className="text-sm text-kampmeyer-neutral-500">
          {decoded < files.length ? `Vorschau lädt… ${decoded}/${files.length}` : `${files.length} Bilder`}
        </span>
      </div>
      <p className="text-sm text-kampmeyer-neutral-500 mb-4">
        Alle Bilder werden mittig auf 4:3 zugeschnitten. Zum Anpassen ein Bild anklicken und den
        Ausschnitt verschieben/zoomen.
        {onNameChange && (
          <>
            {" "}Unter jedem Bild steht der <strong>Dateiname</strong>, z. B. Küche oder Bad – einfach
            anklicken und aus der Liste wählen oder selbst eintippen. Gleiche Namen werden automatisch durchnummeriert, leer bleibt der
            Originalname.
          </>
        )}
      </p>
      {aiStatus &&
        (aiStatus.startsWith("⚠️") ? (
          <p role="status" className="text-sm mb-4 rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2">
            {aiStatus}
          </p>
        ) : (
          <p className="text-sm mb-4 -mt-2 text-kampmeyer-neutral-700">{aiStatus}</p>
        ))}

      {selBmp && selBmp !== "error" && selected && (
        <div className="mb-5 rounded-lg border border-kampmeyer-neutral-200 p-4">
          <div className="text-sm font-medium mb-3 truncate">{selected}</div>
          <div className="flex justify-center">
            <CropFrame
              bitmap={selBmp}
              aspect={aspect}
              boxW={360}
              boxH={Math.round(360 / aspect)}
              initialCrop={crops[selected]}
              onCrop={handleTemp}
              disabled={disabled}
            />
          </div>
          <div className="flex items-center justify-end gap-2 mt-4">
            {crops[selected] && (
              <button onClick={resetOne} className="btn-secondary" disabled={disabled}>
                Zurücksetzen (mittig)
              </button>
            )}
            <button onClick={() => setSelected(null)} className="btn-secondary" disabled={disabled}>
              Schließen
            </button>
            <button onClick={commit} className="btn-primary" disabled={disabled}>
              Ausschnitt übernehmen
            </button>
          </div>
        </div>
      )}

      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px" }}
      >
        {files.map((f) => (
          <Thumb
            key={f.name}
            name={f.name}
            bitmap={bitmaps[f.name]}
            crop={crops[f.name]}
            aspect={aspect}
            active={selected === f.name}
            adjusted={!!crops[f.name]}
            onClick={() => !disabled && bitmaps[f.name] && bitmaps[f.name] !== "error" && setSelected(f.name)}
            nameValue={names[f.name] ?? aiSuggestions[f.name] ?? ""}
            fromAi={names[f.name] === undefined && !!aiSuggestions[f.name]}
            aiPending={names[f.name] === undefined && !!aiPending?.has(f.name)}
            finalName={finalNames[f.name]}
            onNameChange={onNameChange ? (v) => onNameChange(f.name, v) : undefined}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

interface ThumbProps {
  name: string;
  bitmap: ImageBitmap | "error" | undefined;
  crop?: CropRect;
  aspect: number;
  active: boolean;
  adjusted: boolean;
  onClick: () => void;
  nameValue: string;
  fromAi?: boolean;
  aiPending?: boolean;
  finalName?: string;
  onNameChange?: (value: string) => void;
  disabled?: boolean;
}

function Thumb({
  name,
  bitmap,
  crop,
  aspect,
  active,
  adjusted,
  onClick,
  nameValue,
  fromAi,
  aiPending,
  finalName,
  onNameChange,
  disabled,
}: ThumbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const W = 140;
  const H = Math.round(W / aspect);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bitmap || bitmap === "error") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const r = crop ?? centerCropForAspect(bitmap.width, bitmap.height, aspect);
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(
      bitmap,
      r.x * bitmap.width,
      r.y * bitmap.height,
      r.w * bitmap.width,
      r.h * bitmap.height,
      0,
      0,
      W,
      H,
    );
  }, [bitmap, crop, aspect, W, H]);

  return (
    <div
      onClick={onClick}
      draggable={!!bitmap && bitmap !== "error"}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/x-skalierung-name", name);
        e.dataTransfer.effectAllowed = "copy";
      }}
      className="cursor-pointer"
      title="Klicken zum Anpassen · in den Titelbild-Bereich ziehen"
      style={{ border: active ? "2px solid #0b1f3a" : "2px solid transparent", borderRadius: 8 }}
    >
      <div
        style={{ position: "relative", width: "100%", aspectRatio: String(aspect), borderRadius: 6, overflow: "hidden", background: "#e5e7eb" }}
      >
        {bitmap === "error" ? (
          <div className="w-full h-full flex items-center justify-center text-xs text-kampmeyer-neutral-500 px-2 text-center">
            {/\.(heic|heif)$/i.test(name)
              ? "HEIC – keine Browser-Vorschau, wird beim Skalieren verarbeitet"
              : "Vorschau nicht möglich"}
          </div>
        ) : !bitmap ? (
          <div className="w-full h-full flex items-center justify-center text-xs text-kampmeyer-neutral-400">…</div>
        ) : (
          <canvas ref={canvasRef} width={W} height={H} style={{ width: "100%", height: "100%", display: "block" }} />
        )}
        {adjusted && (
          <span
            className="absolute top-1 right-1 text-white text-[10px] px-1.5 py-0.5 rounded"
            style={{ background: "#0b1f3a" }}
          >
            angepasst
          </span>
        )}
      </div>
      {onNameChange ? (
        <>
          <RoomNameInput
            value={nameValue}
            placeholder={aiPending ? "KI erkennt Raum…" : originalBase(name)}
            onChange={onNameChange}
            disabled={disabled}
            label={`Dateiname für ${name}`}
          />
          <div className="text-[11px] text-kampmeyer-neutral-500 mt-1 truncate" title={`Original: ${name}`}>
            {fromAi && (
              <span
                className="inline-block mr-1 px-1 rounded text-[10px] font-medium"
                style={{ background: "#c9a96e", color: "#1a1a18" }}
                title="Vorschlag der KI – zum Ändern einfach überschreiben"
              >
                KI
              </span>
            )}
            → {(finalName ?? originalBase(name)) + ".jpg"}
          </div>
        </>
      ) : (
        <div className="text-[11px] text-kampmeyer-neutral-500 mt-1 truncate" title={name}>
          {name}
        </div>
      )}
    </div>
  );
}
